/**
 * This reducer function handles all the actions related to the top-level objects.
 * @param {Object} state - A portion of the overall Redux state, specifically the
 *      storeIgua.<%=names.camel%> object, representing the current data.
 * @param {Object} action - Action object containing type and other values
 * @return {*} current state or array of language objects
 */

import initialState from '../initialstate';
import * as types from '../../actions/actiontypes';
import Utility from '../../util/utilityfunctions';
import {updateAnnotationAction, HttpStatusCodes, reviewNoteStatusFilter, emptyGuid} from '../../util/uiconstants';
import _ from 'lodash';

export default function rnhNoteDetailReducer(state = initialState.rnhNoteDetail, action) {
	switch (action.type) {
		case types.GET_RNH_NOTE_DETAIL: {
			let response = {...action.response, notesModalOpen: state.notesModalOpen};
			if (response && response.annotations) {
				response.annotations.forEach((n) => (n.gettingReplies = action.gettingReplies));

				if (action.isGroupedByEngagement) {
					let engagements = [];
					response.annotations.forEach((n) => {
						if (!engagements.some((e) => e.id === n.engagementId)) {
							//for each engagement that is not already in the list of engagements we push the engagement and it's notes
							engagements.push({
								id: n.engagementId,
								name: n.engagementDescription,
								openNotesCount: response.annotations.filter(
									(note) => note.engagementId === n.engagementId && note.statusId === reviewNoteStatusFilter.Open
								).length,
								annotations: response.annotations.filter((note) => note.engagementId === n.engagementId)
							});
						}
					});

					//create engagement property and assign to the list of all engagements
					//ordered by engagement name
					response.annotations = engagements.sort((engagement1, engagement2) =>
						engagement1.name.localeCompare(engagement2.name)
					);
				}

				response.payload = action.payload;

				//Fix for modal counts to prevent making several API calls
				if (action.payload && action.payload.isModalRequest && response.otherEngagementsReviewNoteCount > 0) {
					response.allOpenReviewNotesCount = response.annotations.filter(
						(note) => note.engagementId === action.payload.engagementId && note.statusId === reviewNoteStatusFilter.Open
					).length;
					response.myOpenReviewNotesCount = response.annotations.filter(
						(note) =>
							note.engagementId === action.payload.engagementId &&
							(note.assignedToUserId === action.payload.currentUserId ||
								note.createUserId === action.payload.currentUserId) &&
							note.statusId === reviewNoteStatusFilter.Open
					).length;
				}

				if (action.entityId) {
					response.entityId = action.entityId || '';
				}
			}
			return {documentIds: {...state.documentIds}, ...response};
		}
		case types.OPEN_RNH_NOTE_DETAIL_MODAL: {
			return {...state, notesModalOpen: true};
		}
		case types.CLEAR_RNH_NOTE_DETAIL: {
			if (action.useInitial === true) {
				return {annotations: null, notesModalOpen: false, documentIds: state.documentIds};
			} else {
				return {};
			}
		}
		case types.SET_RNH_SELECTED_NOTE_ID_RESET_CONTEXT: {
			return {};
		}
		case types.GET_RNH_NOTE_REPLIES: {
			if (!state || !state.annotations) return state;

			if (action.responseObj.status === HttpStatusCodes.Ok) {
				let newState = _.cloneDeep(state);
				let parentNotes = action.response;
				// look for the annotation and replace with new value
				if (parentNotes && parentNotes.annotations) {
					if (action.isGroupedByEngagement) {
						parentNotes.annotations.forEach((n) => {
							let index;
							newState.annotations.forEach((engagement) => {
								index = engagement.annotations.findIndex((f) => f.id === n.id);
								if (index !== -1) {
									engagement.annotations[index] = n;
								}
							});
						});
					} else {
						parentNotes.annotations.forEach((n) => {
							let index = newState.annotations.findIndex((f) => f.id === n.id);
							if (index !== -1) {
								newState.annotations[index] = n;
							}
						});
					}

					return newState;
				}
			}

			return state;
		}
		case types.POST_RNH_NEW_NOTE: {
			if (!state || !state.annotations) return state;

			if (action.responseObj.status === HttpStatusCodes.Created) {
				// Reducer is being called more than one time.
				const existingNote = state.annotations.find(({id}) => id === action.responseObj?.data?.id);
				if (existingNote) {
					return state;
				}

				let newState = _.cloneDeep(state);

				// increase total
				++newState.totalCount;

				// updating counts
				newState.allOpenReviewNotesCount++;
				if (
					action.noteInfo.currentUser === action.noteInfo.fromUser ||
					action.noteInfo.currentUser === action.noteInfo.toUser
				) {
					newState.myOpenReviewNotesCount++;
				}

				// pushing new note into the list
				newState.annotations.push({
					engagementId: action.noteInfo.engagementId,
					annotationTypeId: action.noteInfo.annotationTypeId,
					assignedToUserName: action.noteInfo.toUserName,
					priorityTypeId: action.noteInfo.priorityTypeId,
					createUserName: action.noteInfo.fromUserName,
					assignedToUserId: action.noteInfo.toUser,
					createUserId: action.noteInfo.fromUser,
					entityUID: action.noteInfo.entityUID,
					entityID: action.noteInfo.entityID,
					dueDate: action.noteInfo.dueDate,
					text: action.noteInfo.comments,
					id: action.responseObj.data.id,
					statusId: action.responseObj.data.statusId,
					createDate: action.responseObj.data.createDate,
					modifiedDate: action.responseObj.data.createDate,
					referenceNumber: action.responseObj.data.referenceNumber,
					editable: action.noteInfo.currentUser === action.noteInfo.fromUser,
					recordingId: action?.noteInfo?.recordingId,
					annotationAttribute: JSON.stringify(action?.noteInfo?.recordingAttribute)
				});

				return {
					...newState,
					documentIds: {
						...state.documentIds,
						[state.entityId]: newState.allOpenReviewNotesCount
					}
				};
			}
			return state;
		}
		case types.POST_RNH_NOTE_REPLY: {
			if (!state || !state.annotations) return state;
			const {response, responseObj, isEngagementComment} = action;

			if ([HttpStatusCodes.Created, HttpStatusCodes.Ok].includes(responseObj.status)) {
				const newState = _.cloneDeep(state);
				const parentNote = !isEngagementComment && response.parentAnnotation;
				let localParentNote = newState?.annotations?.find((n) => n.id === parentNote.id);

				if (!localParentNote) {
					localParentNote = newState?.annotations?.find(
						({engagementCommentGUID}) => engagementCommentGUID === response?.parentEngagementCommentGuid
					);
				}

				// update parent note with new values
				if (isEngagementComment) {
					localParentNote.modifiedDate = response.parentModifyDate;
				} else {
					parentNote.fromUser && (localParentNote.createUserId = parentNote.fromUser);
					parentNote.fromUserName && (localParentNote.createUserName = parentNote.fromUserName);
					parentNote.dueDate && (localParentNote.dueDate = parentNote.dueDate);
					parentNote.toUser && (localParentNote.assignedToUserId = parentNote.toUser);
					parentNote.toUserName && (localParentNote.assignedToUserName = parentNote.toUserName);
					parentNote.statusId && (localParentNote.statusId = parentNote.statusId);
					parentNote.modifiedDate && (localParentNote.modifiedDate = parentNote.modifiedDate);
					parentNote.priorityTypeId && (localParentNote.priorityTypeId = parentNote.priorityTypeId);
				}

				const newReply = {
					author: isEngagementComment
						? `${response.createUserFirstName} ${response.createUserLastName}`
						: response.createUserName,
					authorUserID: response.createUserId,
					id: isEngagementComment ? response.engagementCommentId : response.id,
					isCreatorSelf: true,
					modifiedDate: response.createDate,
					parentId: localParentNote.id,
					text: isEngagementComment ? response.commentText : response.text,
					recordingId: action?.meta?.api?.data?.recordingId,
					annotationAttribute: JSON.stringify(action?.meta?.api?.data?.recordingAttribute)
				};

				// add new reply to array
				localParentNote.replies = [...(localParentNote.replies || []), newReply];
				localParentNote.replyCount = localParentNote.replies.length;

				return newState;
			}

			return state;
		}
		case types.UPDATE_RNH_NOTE_INFO: {
			const userId = action.data.toUser;
			const userName = action.data.userFullName;
			const priorityTypeId = action.data.priorityTypeId;
			const annotationStatusId = action.data.annotationStatusId;
			const dueDate = action.data.dueDate;
			const modifiedDate = action.data.modifiedDate;

			let newState = _.cloneDeep(state);
			let element = _.find(newState.annotations, (a) => a.id === action.selectedNoteId);

			if (element) {
				if (userId) {
					element.assignedToUserId = userId;
					element.assignedToUserName = userName;
				}
				if (priorityTypeId) {
					element.priorityTypeId = priorityTypeId;
				}
				if (annotationStatusId) {
					element.statusId = annotationStatusId;
				}
				if (dueDate) {
					element.dueDate = dueDate;
				}
				element.modifiedDate = modifiedDate;
			}
			return newState;
		}
		case types.UPDATE_RNH_NOTE_DETAIL: {
			if (!state || !state.annotations) return state;

			if (action.responseObj.status === HttpStatusCodes.Ok) {
				let newState = _.cloneDeep(state);
				let note, oldStatus;
				if (action.data.updateAction === updateAnnotationAction.editReply) {
					// find parent note and update modified date
					let parentNote;
					newState.annotations.every((a) => {
						note = a.replies ? a.replies.find((r) => r.id === action.noteId) : null;
						if (note) {
							parentNote = a;
							return false;
						}
						return true;
					});

					updateNoteData(parentNote, {}, action.sendCommentData);
				} else {
					note = newState.annotations.find((a) => a.id === action.noteId);
					oldStatus = note.statusId;
				}

				updateNoteData(note, action.data, action.sendCommentData);

				newState.allOpenReviewNotesCount = newState.annotations.filter(
					(item) => item.statusId === reviewNoteStatusFilter.Open && item.engagementId === action.data.engagementId
				).length;
				newState.myOpenReviewNotesCount = newState.annotations.filter(
					(item) =>
						item.statusId === reviewNoteStatusFilter.Open &&
						(item.assignedToUserId === action.currentUser || item.createUserId === action.currentUser) &&
						item.engagementId === action.data.engagementId
				).length;

				return {
					...newState,
					documentIds: {
						...state.documentIds,
						[state.entityId]: newState.allOpenReviewNotesCount
					}
				};
			} else if (action.error.response.status === HttpStatusCodes.NotFound) {
				let newState = _.cloneDeep(state);
				newState.parentWasDeleted = true;

				return newState;
			}

			return state;
		}
		case types.DELETE_RNH_NOTE_DETAIL: {
			if (!state || !state.annotations) return state;
			if (action.responseObj.status === HttpStatusCodes.NoContent || action.responseObj.status === HttpStatusCodes.Ok) {
				let newState = _.cloneDeep(state);
				// find parent note and update modified date
				let parentNote;

				newState.annotations.every((a) => {
					let reply = a.replies ? a.replies.find((r) => r.id === action.data.noteId) : null;
					if (reply) {
						_.remove(a.replies, (r) => {
							return r.id === action.data.noteId;
						});
						parentNote = a;
						parentNote.modifiedDate = action.data.modifiedDate;
						parentNote.replyCount--;
						return false;
					} else if (a.id === action.data.noteId) {
						newState.parentWasDeleted = true;
					}
					return true;
				});
				return newState;
			} else if (action.error.response.status === HttpStatusCodes.Conflict) {
				// Bifurcation only for concurrency scenario when another user changes open status from parent note.
				return state;
			} else if (action.error.response.status === HttpStatusCodes.NotFound) {
				// Bifurcation only for concurrency scenario when another user deletes parent note.
				let newState = _.cloneDeep(state);
				newState.parentWasDeleted = true;
				return newState;
			}
			return state;
		}
		case types.DELETE_RNH_PARENT_NOTE: {
			if (!state || !state.annotations) return state;
			if (
				action.responseObj.status === HttpStatusCodes.NoContent ||
				action.responseObj.status === HttpStatusCodes.Ok ||
				action.error.response.status === HttpStatusCodes.NotFound
			) {
				let newState = _.cloneDeep(state);
				_.remove(newState.annotations, (a) => {
					return a.id === action.data.noteId;
				});
				newState.parentWasDeleted = true;
				return newState;
			}
			return state;
		}
		case types.GET_FORM_BODY_COMMENT: {
			if (action.responseObj.status === HttpStatusCodes.Ok) {
				let _detailNote = _.cloneDeep(state);
				if (_detailNote.annotations && _detailNote.annotations[0]) {
					_detailNote.annotations[0].description = action.response;
				}
				return _detailNote;
			}
			return state;
		}
		case types.ARES_GET_DOCUMENT_BY_ID: {
			if (action.response) {
				return {
					...state,
					documentIds: {
						...state.documentIds,
						[action.response.id]: action.response.engagementReviewNoteCount
					}
				};
			}
			break;
		}
		default:
			return state;
	}
}

const updateNoteData = (note, data, isEngagementComment = false) => {
	if (note) {
		if (data.toUser) {
			note.assignedToUserId = data.toUser;
			note.assignedToUserName = data.toUserName;
		}
		if (data.assignedToUserId) {
			note.assignedToUserId = data.assignedToUserId;
		}
		if (data.assignedToName) {
			note.assignedToUserName = data.assignedToName;
		}
		if (data.priorityTypeId) {
			note.priorityTypeId = data.priorityTypeId;
		}
		if (data.annotationStatusId) {
			note.statusId = isEngagementComment
				? Utility.getAnnotationIdFromComment(data.annotationStatusId)
				: data.annotationStatusId;
		}
		if (data.engagementCommentStatusId) {
			note.statusId = Utility.getAnnotationIdFromComment(data.engagementCommentStatusId);
		}
		if (data.dueDate) {
			note.dueDate = data.dueDate;
		}
		if (data.comments !== undefined) {
			note.text = data.comments;
		}
		if (data.commentText) {
			note.text = data.commentText;
		}
		if (data.statusChangeUserName) {
			note.statusChangeUserName = data.statusChangeUserName;
			note.statusChangeDate = new Date().toISOString();
		}
		if (note.isCallingAPI) {
			note.isCallingAPI = false;
		}

		if (
			!_.isEmpty(data) &&
			(data.updateAction === updateAnnotationAction.editNote || data.updateAction === updateAnnotationAction.editReply)
		) {
			if (data?.recordingId && data?.recordingId !== emptyGuid) {
				note.recordingId = data.recordingId;
				if (data.recordingAttribute) {
					note.annotationAttribute = JSON.stringify(data.recordingAttribute);
				}
			} else {
				note.recordingId = emptyGuid;
				note.annotationAttribute = '';
			}
		}

		note.modifiedDate = data.modifiedDate || new Date().toISOString();
	}
};
